import React, { useEffect, useState } from "react";
import Input from "../FormInputs";
import { ReactComponent as CloseIcon } from "../../../Assets/Icons/popupCloseIcon.svg";
import { ReactComponent as DownArrow } from "../../../Assets/Icons/greyDownArrow.svg";
import Selector from "../Selector";
import { Slider, Modal, Upload } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useDropzone } from "react-dropzone";
import RadioSelection from "../RadioSelection";
import InputTextArea from "../InputTextArea";
import { useFormik } from "formik";
import Cropper from "react-easy-crop";
import { getCroppedImgFile } from "../../../utils/cropImage";
import PrimaryButton from "../PrimaryButton";
import GreyButton from "../GreyButton";
import { offerCreationSchema } from "../../../utils/validationSchemas";
import { useDispatch, useSelector } from "react-redux";
import { createOffer } from "../../../services/offerService";
import { uploadFile } from "../../../utils/s3";
import toast from "../../../utils/toast";
import CSVUpload from "./CSVUpload";
import { useOfferFormContext } from "./OfferFormContext";

const OfferCreation = ({
  stepIndex,
  setStepIndex,
  handlePrevious,
  setPayloadData,
  editOfferData,
  isEdit,
  selectedMerchant,
  handleSaveAndExit,
}) => {
  const dispatch = useDispatch();
  const loading = useSelector((state) => state?.offerReducer?.loading);
  const offerData = useSelector((state) => state?.offerReducer?.createdOffer);
  const { registerNextAction, setIsNextLoading } = useOfferFormContext();

  // Unified Images State
  // Array of { url: string (local or s3), file: File, isThumbnail: boolean, s3Url: string }
  const [imageList, setImageList] = useState([]);
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const [selectedFileForCrop, setSelectedFileForCrop] = useState(null);
  const [previewUrlForCrop, setPreviewUrlForCrop] = useState(null);

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const [activeUploads, setActiveUploads] = useState(0); // aggregate uploading counter
  const isUploading = activeUploads > 0;

  // Separate loading states for each button
  const [isAddingImage, setIsAddingImage] = useState(false);
  const [isAddingThumbnail, setIsAddingThumbnail] = useState(false);

  const [fileloading, setFileLoading] = useState(false);
  const [fileUrl, setFileUrl] = useState(null);
  const [fileData, setFileData] = useState();

  const validateFile = (data) => {
    var idxDot = data.lastIndexOf(".") + 1;
    var extFile = data.substr(idxDot, data.length).toLowerCase();
    if (extFile === "csv" || extFile === "xlsx" || extFile === "xls") {
      return true;
    } else {
      return false;
    }
  };
  const validateOfferCodeFormat = (code) => {
    const codePattern = /^[A-Z0-9]{7}$/;
    return codePattern.test(code);
  };
  const handleAcceptedFiles = async (event) => {
    const file = event.target.files[0];
    console.log("file", file);
    if (validateFile(file?.name)) {
      setFileLoading(true);

      // Read the CSV file
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target.result;
        const lines = text.split("\n");
        // Remove header row and empty lines, then trim each code
        const codes = lines
          .slice(1) // Skip header row
          .filter((line) => line.trim() !== "") // Remove empty lines
          .map((line) => line.trim()); // Trim whitespace

        if (codes.length > 0) {
          // Validate each code format
          const invalidCodes = codes.filter(
            (code) => !validateOfferCodeFormat(code)
          );

          if (invalidCodes.length > 0) {
            toast.error(
              `Invalid code format found: ${invalidCodes.join(
                ", "
              )}. Codes must be 7 characters (letters and numbers only).`
            );
            handleRejectedFiles(file);
            setFileLoading(false);
            return;
          }
          setFieldValue("offerCodes", codes);
          setFieldValue("document", file?.name);
        } else {
          toast.error("No valid offer codes found in the CSV file");
          handleRejectedFiles(file);
        }
        setFileLoading(false);
      };
      reader.onerror = () => {
        toast.error("Error reading the CSV file");
        handleRejectedFiles(file);
        setFileLoading(false);
      };
      reader.readAsText(file);
    } else {
      toast.error(`Only .csv, .xls, and .xlsx files are allowed!`);
      handleRejectedFiles(file);
    }
  };

  const handleRejectedFiles = (files) => {
    const file = files[0];
    setFileData(null);
    setFieldValue("document", null);
    files[0]?.errors?.forEach((error) => {
      toast.error(error.message);
    });
  };

  const handleClear = () => {
    setFileUrl(null);
    setFileData(null);
    setFieldValue("document", "");
    setFieldValue("offerCodes", []);
  };

  const fillUpForm = (data) => {
    setFieldValue("title", data?.title !== null ? data?.title : "");
    setFieldValue("offerType", data?.offerType || "INSTORE_SWOODLE_OFFER");
    setFieldValue("onlineOption", data?.onlineOption || "ONLINE");
    setFieldValue("websiteURL", data?.websiteURL || null);
    setFieldValue("offerCodes", data?.offerCodes || []);
    switch (data?.offerDiscountType) {
      case "FREE_OFFER":
        setFieldValue("offerDiscountType", "FREE_OFFER");
        break;
      case "DISCOUNTED_PERCENT":
        setFieldValue("offerDiscountType", "DISCOUNTED_PERCENT");
        break;
      case "FIXED_DOLLAR":
        setFieldValue("offerDiscountType", "FIXED_DOLLAR");
        break;
      case "BUY_ONE_GET_ONE_FREE":
        setFieldValue("offerDiscountType", "BUY_ONE_GET_ONE_FREE");
        break;
      case "BUNDLE_OFFER":
        setFieldValue("offerDiscountType", "BUNDLE_OFFER");
        break;
      default:
        setFieldValue("offerDiscountType", "");
    }
    setRangeValue(data?.discountRange !== null ? data?.discountRange : 25);
    setFieldValue(
      "discountRange",
      data?.discountRange !== 0 ? data?.discountRange : 25
    );
    setFieldValue(
      "fixedDollarAmount",
      data?.fixedDollarAmount !== 0 ? data?.fixedDollarAmount : 0
    );
    setFieldValue(
      "description",
      data?.description !== null ? data?.description : ""
    );

    // Populate unified imageList
    const newImageList = [];
    if (data?.thumbnailImage) {
      newImageList.push({
        url: process.env.REACT_APP_S3_URL + data.thumbnailImage,
        s3Url: data.thumbnailImage,
        isThumbnail: true,
      });
    }
    if (data?.previewImages?.length > 0) {
      data.previewImages.forEach((p) => {
        newImageList.push({
          url: process.env.REACT_APP_S3_URL + p,
          s3Url: p,
          isThumbnail: false,
        });
      });
    }
    setImageList(newImageList);

    setFieldValue("thumbnailImage", data?.thumbnailImage !== null ? data?.thumbnailImage : null);
    setFieldValue("previewImages", data?.previewImages || []);
  };

  // Sync imageList back to formik values
  useEffect(() => {
    const thumbnail = imageList.find((img) => img.isThumbnail);
    const previews = imageList
      .filter((img) => !img.isThumbnail)
      .map((img) => img.s3Url)
      .filter(Boolean);

    if (thumbnail?.s3Url !== formik.values.thumbnailImage) {
      setFieldValue("thumbnailImage", thumbnail ? thumbnail.s3Url : null);
    }
    // Deep comparison or simple join comparison to avoid re-renders
    if (previews.join(",") !== (formik.values.previewImages || []).join(",")) {
      setFieldValue("previewImages", previews);
    }
  }, [imageList]);

  const handleImageRemove = (index) => {
    const newImageList = [...imageList];
    const removedItem = newImageList.splice(index, 1)[0];

    // If we removed the thumbnail, pick a new one if possible
    if (removedItem.isThumbnail && newImageList.length > 0) {
      newImageList[0].isThumbnail = true;
    }
    setImageList(newImageList);
  };

  const handleSetThumbnail = (index) => {
    const newImageList = imageList.map((img, i) => ({
      ...img,
      isThumbnail: i === index,
    }));
    // Move the thumbnail to the first position as per user request
    const thumbnailItem = newImageList.splice(index, 1)[0];
    newImageList.unshift(thumbnailItem);
    setImageList(newImageList);
  };

  const onDrop = async (acceptedFiles) => {
    if (imageList.length + acceptedFiles.length > 5) {
      toast.error("You can only upload up to 5 images.");
      return;
    }

    const newItems = [];
    for (const file of acceptedFiles) {
      const localUrl = URL.createObjectURL(file);
      setSelectedFileForCrop(file);
      setPreviewUrlForCrop(localUrl);
      setIsCropModalOpen(true);
      break; // Handle one at a time for cropping
    }
  };

  const onCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: "image/*",
    multiple: true,
  });

  const handleCropSave = async (asThumbnail = false) => {
    if (!selectedFileForCrop || !croppedAreaPixels) return;

    // Set the appropriate loading state
    if (asThumbnail) {
      setIsAddingThumbnail(true);
    } else {
      setIsAddingImage(true);
    }

    setActiveUploads((prev) => prev + 1);
    try {
      // Get the cropped file
      const croppedFile = await getCroppedImgFile(
        previewUrlForCrop,
        croppedAreaPixels,
        0,
        { horizontal: false, vertical: false },
        selectedFileForCrop.name
      );

      const s3Url = await uploadFile(croppedFile, croppedFile.name);
      if (s3Url) {
        const newItem = {
          url: URL.createObjectURL(croppedFile),
          s3Url: s3Url,
          isThumbnail: asThumbnail || imageList.length === 0,
        };

        const newList = [...imageList];
        if (newItem.isThumbnail) {
          // Unset previous thumbnails
          newList.forEach((img) => (img.isThumbnail = false));
          newList.unshift(newItem);
        } else {
          newList.push(newItem);
        }
        setImageList(newList.slice(0, 5));
        toast.success("Image added successfully");
      }
    } catch (error) {
      console.error("Crop/Upload error:", error);
      toast.error("Failed to upload image");
    } finally {
      setActiveUploads((prev) => Math.max(0, prev - 1));
      setIsAddingImage(false);
      setIsAddingThumbnail(false);
      setIsCropModalOpen(false);
      setSelectedFileForCrop(null);
      setPreviewUrlForCrop(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    }
  };
  useEffect(() => {
    // Logic to determine which data source to use:
    // 1. If we are editing, ordinarily we might look at editOfferData.
    // 2. BUT, if we have made changes in this session (persisted in offerData Redux state),
    //    and that offerData corresponds to the same offer ID, we should prioritize offerData.
    // 3. For new offers (!isEdit), we always use offerData.

    let sourceData = offerData;

    if (isEdit && editOfferData) {
      if (offerData?._id === editOfferData?._id) {
        // Check if offerData has meaningful data (e.g. title) to confirm it's not just an empty init
        sourceData = (offerData.title || offerData.offerType) ? offerData : editOfferData;
      } else {
        sourceData = editOfferData;
      }
    } else {
      // Not edit mode, just use offerData
      sourceData = offerData;
    }

    if (sourceData) {
      fillUpForm(sourceData);
    }
  }, [offerData?._id, editOfferData?._id, isEdit]); // Reduced dependencies to avoid loop, trigger only on ID/mode change or mount

  const initialValues = {
    title: "",
    offerType: "INSTORE_SWOODLE_OFFER",
    offerDiscountType: "",
    onlineOption: "",
    offerCodes: [],
    fixedDollarAmount: null,
    description: "",
    discountRange: 25,
    thumbnailImage: null,
    previewImages: [],
    document: "",
    websiteURL: null,
  };

  const handleNextStep = async () => {
    console.log("handleNextStep called");
    console.log("Current form values:", { offerCodes, onlineOption, offerType });

    const processedCodes = processOfferCodes();
    console.log("Processed codes:", processedCodes);

    const currentPayload = {
      id: offerData?._id,
      merchantId: selectedMerchant?._id,
      title: title,
      offerType: offerType,
      offerDiscountType: offerDiscountType,
      onlineOption: onlineOption,
      discountRange: discountRange,
      fixedDollarAmount: fixedDollarAmount,
      offerCodes: processedCodes,
      thumbnailImage: thumbnailImage,
      previewImages: previewImages,
      ...(description?.trim()
        ? { description: description.trim() }
        : {}),
      websiteURL: websiteURL,
      isCreated: false,
    };

    console.log("Sending payload to createOffer:", currentPayload);

    const createPromise = await dispatch(
      createOffer({ ...currentPayload, offerId: offerData?._id })
    )?.unwrap();
    if (createPromise?.success === true) {
      console.log("createOffer successful, moving to next step");
      setStepIndex(stepIndex + 1);
    } else {
      console.log("createOffer failed:", createPromise);
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema: offerCreationSchema,

    onSubmit: async (values) => {
      console.log("Form onSubmit called with values:", values);
      console.log("Form errors:", formik.errors);
      console.log("Form touched:", formik.touched);
      await handleNextStep();
    },
  });

  // Register the Next action for the Layout's Next button
  useEffect(() => {
    registerNextAction(() => attemptNext());
  }, [registerNextAction, formik.values, imageList]);

  // Sync loading state with Layout's Next button
  useEffect(() => {
    setIsNextLoading(loading);
  }, [loading, setIsNextLoading]);

  const attemptNext = async () => {
    const validationErrors = await formik.validateForm();
    if (!formik.values.offerType) {
      formik.setTouched({ ...formik.touched, offerType: true });
      formik.setFieldError("offerType", "Offer Type Selection is required");
    }
    if (Object.keys(validationErrors || {}).length > 0 || !formik.values.offerType) {
      scrollToFirstError({ ...validationErrors, ...(formik.values.offerType ? {} : { offerType: "Offer Type Selection is required" }) });
      return;
    }
    handleSubmit();
  };

  const {
    title,
    offerType,
    onlineOption,
    offerDiscountType,
    discountRange,
    fixedDollarAmount,
    description,
    offerCodes,
    websiteURL,
    document,
    thumbnailImage,
    previewImages,
  } = formik.values;

  const {
    handleChange,
    handleBlur,
    errors,
    touched,
    setFieldValue,
    handleSubmit,
    // setValues,
  } = formik;

  const scrollToFirstError = (formErrors) => {
    const priority = [
      "title",
      "offerType",
      "offerDiscountType",
      "description",
      "discountRange",
      "fixedDollarAmount",
      "thumbnailImage",
      "previewImages",
      "websiteURL",
      "offerCodes",
    ];
    let targetField = priority.find(
      (key) => formErrors && Object.prototype.hasOwnProperty.call(formErrors, key)
    );
    if (!targetField) {
      const keys = Object.keys(formErrors || {});
      targetField = keys.length ? keys[0] : null;
    }
    if (!targetField) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const doc = window.document;
    let el =
      doc.querySelector(`[data-field="${targetField}"]`) ||
      doc.querySelector(`[name="${targetField}"]`) ||
      doc.querySelector(`input[name="${targetField}"]`) ||
      doc.querySelector(`textarea[name="${targetField}"]`) ||
      doc.querySelector(`select[name="${targetField}"]`);
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      if (typeof el.focus === "function") {
        try {
          el.focus();
        } catch (_) { }
      }
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // validation will be applied inline on Next click for this step

  const handleOfferCodesChange = (e) => {
    const value = e.target.value;
    // Store the raw input value, we'll process it on blur or form submission
    setFieldValue("offerCodes", value);
  };

  const processOfferCodes = () => {
    if (typeof offerCodes === "string") {
      const codesArray = offerCodes
        .split(",")
        .map((code) => code.trim())
        .filter((code) => code !== "");
      setFieldValue("offerCodes", codesArray);
      return codesArray;
    }
    return offerCodes;
  };

  const handleOfferCodesBlur = (e) => {
    processOfferCodes(); // Reuse the same processing logic
    handleBlur(e);
  };

  const payload = {
    id: offerData?._id,
    merchantId: selectedMerchant?._id,
    title: title,
    offerType: offerType,
    offerDiscountType: offerDiscountType,
    // offerDiscountType: offerDiscountType === "Free Offer"
    //   ? "FREE_OFFER"
    //   : offerDiscountType === "Discounted Offer"
    //     ? "DISCOUNTED_PERCENT"
    //     : offerDiscountType === "Fixed Dollar Discount"
    //       ? "FIXED_DOLLAR"
    //       : offerDiscountType === "Buy One Get One Free Offer"
    //         ? "BUY_ONE_GET_ONE_FREE"
    //         : offerDiscountType === "Bundle Offer"
    //           ? "BUNDLE_OFFER"
    //           : "",
    onlineOption: onlineOption,
    discountRange: discountRange,
    fixedDollarAmount: fixedDollarAmount,
    offerCodes: offerCodes,
    thumbnailImage: thumbnailImage,
    previewImages: previewImages,
    ...(description?.trim()
      ? { description: description.trim() }
      : {}),
    websiteURL: websiteURL,
    isCreated: false,
  };

  // console.log("Payload Data ==>>", previewImagesUrl);

  useEffect(() => {
    setPayloadData(payload);
    // console.log("Preview Images ==>>", formik.values.previewImages)
  }, [
    offerData?._id,
    title,
    offerType,
    offerDiscountType,
    discountRange,
    description,
    thumbnailImage,
    previewImages,
    offerCodes, // Add offerCodes to dependencies
    onlineOption, // Add onlineOption to dependencies
  ]);

  const offerTypeOptions = [
    { label: "Instore Swoodle Offer", value: "INSTORE_SWOODLE_OFFER" },
    { label: "Online Offer", value: "ONLINE_OFFER" },
  ];

  // Enhanced options with descriptions for the new Card UI
  const offerDiscountTypeOptions = [
    { label: "Free Offer", value: "FREE_OFFER", desc: "Give a product for free." },
    { label: "Discounted Offer", value: "DISCOUNTED_PERCENT", desc: "Apply a percentage discount on the price." },
    { label: "Fixed Dollar Discount", value: "FIXED_DOLLAR", desc: "Apply a fixed amount off on price." },
    { label: "Buy One Get One Free Offer", value: "BUY_ONE_GET_ONE_FREE", desc: "Offer a free product with purchase." },
    { label: "Bundle Offer", value: "BUNDLE_OFFER", desc: "Offer multiple products together." },
  ];

  const handleTypeChange = (e) => {
    setFieldValue("offerType", e);
    // Clear fields irrelevant to INSTORE offer
    if (e === "INSTORE_SWOODLE_OFFER") {
      setFieldValue("onlineOption", "");
      setFieldValue("websiteURL", "");
      setFieldValue("offerCodes", []);
      setFieldValue("document", "");
      if (stepIndex > 5) {
        setStepIndex(6);
      }
    }
  };

  const handleDiscountTypeChange = (value) => {
    setFieldValue("offerDiscountType", value);
    // Clear fields irrelevant to the selected discount type
    if (value === "DISCOUNTED_PERCENT") {
      setFieldValue("fixedDollarAmount", null);
    } else if (value === "FIXED_DOLLAR") {
      setFieldValue("discountRange", 25);
    } else if (
      value === "FREE_OFFER" ||
      value === "BUY_ONE_GET_ONE_FREE" ||
      value === "BUNDLE_OFFER"
    ) {
      setFieldValue("fixedDollarAmount", null);
      setFieldValue("discountRange", 25);
    }
  };

  // const radioOptions = ["Featured Offers", "Hot Offers"];
  const radioOnlineOptions = [
    {
      title: "Online",
      value: "ONLINE",
      content:
        "Users will be redirected to your website to claim this offer.",
    },
    {
      title: "Online with Code",
      value: "ONLINE_WITH_CODE",
      content:
        "Users can copy this code and apply it on your website.",
    },
  ];
  const [rangeValue, setRangeValue] = useState(25);
  const [isSliderDragging, setIsSliderDragging] = useState(false);
  const [tagInput, setTagInput] = useState("");

  const handleTagKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      const val = tagInput.trim().replace(/,$/, "").toUpperCase();
      if (val) {
        const currentCodes = Array.isArray(offerCodes) ? offerCodes : [];
        if (!currentCodes.includes(val)) {
          setFieldValue("offerCodes", [...currentCodes, val]);
        }
        setTagInput("");
      }
    } else if (e.key === "Backspace" && tagInput === "" && Array.isArray(offerCodes) && offerCodes.length > 0) {
      const newCodes = [...offerCodes];
      newCodes.pop();
      setFieldValue("offerCodes", newCodes);
    }
  };

  const handleRemoveTag = (codeToRemove) => {
    if (Array.isArray(offerCodes)) {
      setFieldValue("offerCodes", offerCodes.filter((c) => c !== codeToRemove));
    }
  };

  const handleRangeChange = (value) => {
    setIsSliderDragging(true);
    const parsedValue = Math.min(Math.max(0, Number(value) || 0), 100);
    setRangeValue(parsedValue);
    setFieldValue("discountRange", parsedValue);
  };

  const handleRangeAfterChange = (value) => {
    setIsSliderDragging(false);
  };

  useEffect(() => {
    if (offerDiscountType === "DISCOUNTED_PERCENT") {
      // Initialize the range values when discount type is selected
      const initialRange = discountRange || 25;
      setRangeValue(initialRange);
      setFieldValue("discountRange", initialRange);
    }
  }, [offerDiscountType]);

  const isNextButtonBusy = Boolean(loading) || isUploading;
  const nextButtonLabel = isNextButtonBusy ? (
    <span className="flex items-center justify-center gap-2">
      <span className="offer-btn-spinner" />
      <span>{isUploading ? "Uploading..." : "Processing..."}</span>
    </span>
  ) : (
    "Next"
  );

  return (
    <div className="w-full flex flex-col items-center justify-center mt-[60px] offer-builder-step3">
      <div className="w-full max-w-[1100px] relative">
        <div className="absolute top-[-70px] right-0">
          <button
            type="button"
            className="px-6 py-2 border border-[#00ADEF] text-[#00ADEF] rounded-[5px] font-poppins font-[500] text-[14px] hover:bg-[#F0FBFF] transition-colors"
            onClick={() => {
              if (typeof handleSaveAndExit === 'function') {
                handleSaveAndExit();
              } else {
                console.warn('handleSaveAndExit not provided to OfferCreation');
              }
            }}
          >
            Save and Exit
          </button>
        </div>
        <div className="w-full max-w-[650px] mx-auto offerform">
          <h2 className="text-[36px] font-[600] text-[#303030] font-poppins text-left">
            Create Your <span className="text-[#00ADEF]">Offer</span>
          </h2>
          <p className="font-poppins font-[400] text-[18px] text-[#767676] text-left mt-1 mb-10">
            Share the essential details to help customers understand your offer.
          </p>

          {/* 1. Offer Title */}
          {/* 1. Offer Title */}
          <div className="mt-5 w-full">
            <label className="font-[600] font-poppins text-[#303030] text-[14px] block mb-[4px]">
              Offer Title<span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="title"
              value={title}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Enter offer title"
              className="w-full h-[45px] px-[13px] rounded-[5px] bg-[#FFFFFF] border border-[#D9D9D9] text-[#303030] text-[14px] font-[400] outline-none font-poppins"
            />
            {errors.title && touched.title && (
              <div className="ms-1 text-start w-full">
                <span className="text-red-600 text-[8px] sm:text-[10px] md:text-[12px] font-[600]">
                  {errors.title}
                </span>
              </div>
            )}
          </div>

          {/* 2. Offer Description */}
          <div className="mt-5 w-full">
            <label className="font-[600] text-[#303030] font-poppins text-[14px] block mb-[4px]">
              Offer Description
            </label>
            <div className="relative">
              <input
                type="text"
                name="description"
                value={description || ""}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter offer description"
                maxLength={400}
                className="w-full h-[45px] px-[13px] pr-[60px] rounded-[5px] bg-[#FFFFFF] border border-[#D9D9D9] text-[#303030] text-[14px] font-[400] outline-none font-poppins"
              />
              <span className="absolute right-[13px] top-1/2 -translate-y-1/2 text-[#D9D9D9] text-[12px] font-poppins pointer-events-none">
                {description?.length || 0}/400
              </span>
            </div>
          </div>

          {/* 3. Offer Images (Unified) */}
          <div className="mt-8">
            <p className="font-poppins font-[600] text-[14px] text-[#303030]">
              Offer Images<span className="text-red-500">*</span>
            </p>
            <p className="font-poppins font-[400] text-[14px] text-[#767676] mt-1 mb-4">
              Enhance your offer by uploading up to 5 images.
            </p>

            <div className="flex flex-wrap gap-4 items-center">
              {imageList.map((img, index) => (
                <div
                  key={index}
                  className="relative w-[315px] h-[165px] rounded-[10px] overflow-visible border border-[#D9D9D9] bg-[#F9F9F9] group"
                >
                  <img
                    src={img.url}
                    className="w-full h-full object-cover rounded-[10px]"
                    alt={`Offer ${index}`}
                  />
                  {/* Remove button - Cyan Circle with White X positioned on edge */}
                  <button
                    type="button"
                    className="absolute -top-[10px] -right-[10px] w-[28px] h-[28px] bg-[#00BFFF] rounded-full shadow-lg transition-all z-20 hover:scale-110 flex items-center justify-center p-0"
                    onClick={() => handleImageRemove(index)}
                  >
                    <CloseIcon style={{ width: 10, height: 10, color: "white", display: 'block', margin: '0' }} />
                  </button>

                  {/* Thumbnail Indicator / Action */}
                  {img.isThumbnail ? (
                    <div className="absolute bottom-0 left-0 right-0 bg-[#00ADEF] text-white text-[10px] font-poppins text-center py-0.5 z-10 rounded-b-[10px]">
                      Thumbnail
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="absolute bottom-0 left-0 right-0 bg-black/40 text-white text-[10px] font-poppins text-center py-0.5 opacity-0 group-hover:opacity-100 transition-opacity z-10 rounded-b-[10px]"
                      onClick={() => handleSetThumbnail(index)}
                    >
                      Set as Thumbnail
                    </button>
                  )}
                </div>
              ))}

              {imageList.length < 5 && (
                <div
                  {...getRootProps()}
                  className="w-[315px] h-[165px] border border-solid border-[#D9D9D9] hover:border-[#00ADEF] transition-colors rounded-[10px] flex flex-col items-center justify-center cursor-pointer bg-white"
                >
                  <input {...getInputProps()} />
                  <PlusOutlined style={{ fontSize: 24, color: "#D9D9D9" }} />
                  <span className="text-[14px] text-[#767676] mt-1 font-poppins">
                    Add Image
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Cropping / Adjust Modal - Redesigned to match Screenshot 2 */}
          <Modal
            title={null}
            open={isCropModalOpen}
            onCancel={() => {
              setIsCropModalOpen(false);
              setCrop({ x: 0, y: 0 });
              setZoom(1);
            }}
            closeIcon={
              <div className="offer-modal-close-btn">
                <CloseIcon style={{ width: '14px', height: '14px', color: 'white' }} />
              </div>
            }
            centered
            width={600}
            className="offer-adjust-image-modal"
            transitionName="ant-fade"
            maskTransitionName="ant-fade"
            footer={null}
          >
            <div className="flex flex-col items-center gap-4 py-6 px-6">
              <h3 className="w-full text-left font-poppins font-[600] text-[20px] text-[#303030] mb-1">
                Adjust Image
              </h3>
              {previewUrlForCrop && (
                <div className="relative w-full h-[300px] rounded-[10px] overflow-hidden bg-[#1a1a1a] shadow-inner">
                  <Cropper
                    image={previewUrlForCrop}
                    crop={crop}
                    zoom={zoom}
                    aspect={315 / 165}
                    onCropChange={setCrop}
                    onCropComplete={onCropComplete}
                    onZoomChange={setZoom}
                  />
                </div>
              )}
              <div className="w-full flex items-center gap-4 mt-2">
                <span className="font-poppins text-[14px] font-[500] text-[#767676]">Zoom</span>
                <Slider
                  className="flex-grow offer-crop-slider"
                  min={1}
                  max={3}
                  step={0.01}
                  value={zoom}
                  onChange={(v) => setZoom(v)}
                  tooltip={{ open: false }}
                />
              </div>

              <div className="mt-4 flex flex-col items-center text-center">
                <h4 className="font-poppins font-[600] text-[16px] text-[#303030] mb-2">
                  Perfect! How would you like to use this image?
                </h4>
                <p className="font-poppins font-[400] text-[13px] text-[#767676] max-w-[500px] leading-relaxed mb-6">
                  A thumbnail is the primary image shown to customers. You can change this later by clicking "Set as Thumbnail".
                </p>
                <div className="flex items-center justify-center gap-3 w-full">
                  <button
                    className="px-8 py-2 rounded-[5px] text-[#767676] font-poppins font-[500] text-[14px] hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => {
                      setIsCropModalOpen(false);
                      setCrop({ x: 0, y: 0 });
                      setZoom(1);
                    }}
                    disabled={isAddingImage || isAddingThumbnail}
                  >
                    Cancel
                  </button>
                  <button
                    className="px-8 py-2 rounded-[5px] text-[#00ADEF] border border-[#00ADEF] font-poppins font-[500] text-[14px] hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 min-w-[120px]"
                    onClick={() => handleCropSave(false)}
                    disabled={isAddingImage || isAddingThumbnail}
                  >
                    {isAddingImage ? (
                      <>
                        <span className="offer-inline-spinner-small" />
                        <span>Adding...</span>
                      </>
                    ) : (
                      "Add Image"
                    )}
                  </button>
                  <button
                    className="px-8 py-2 rounded-[5px] bg-[#00ADEF] text-white font-poppins font-[500] text-[14px] hover:bg-[#0095d1] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 min-w-[150px]"
                    onClick={() => handleCropSave(true)}
                    disabled={isAddingImage || isAddingThumbnail}
                  >
                    {isAddingThumbnail ? (
                      <>
                        <span className="offer-inline-spinner-small" />
                        <span>Processing...</span>
                      </>
                    ) : (
                      "Use as Thumbnail"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </Modal>

          {/* 4. Offer Type */}
          <div className="mt-8 w-full">
            <p className="text-[14px] font-[600] text-[#303030] font-poppins mb-[4px]">
              Offer Type<span className="text-red-500">*</span>
            </p>
            <Selector
              options={offerTypeOptions}
              value={offerType}
              onChange={handleTypeChange}
              placeholder="Select Offer Type"
              className="w-full font-poppins"
              radius="5px"
              suffixIcon={<DownArrow />}
              label=""
            />
          </div>

          {/* 4.5. Online Offer Details (Consolidated Card Design) */}
          {offerType === "ONLINE_OFFER" && (
            <div className="bg-white border border-[#F0F0F0] rounded-[15px] p-6 mt-6 shadow-[0px_8px_30px_0px_rgba(0,0,0,0.06)] relative z-10 w-full mb-8">
              <h3 className="text-[16px] font-[600] text-[#303030] font-poppins mb-4">
                Online Offer Details
              </h3>

              {/* Website URL Input */}
              <div className="w-full mb-6" onClick={(e) => e.stopPropagation()}>
                <label className="font-[600] font-poppins text-[#303030] text-[14px] block mb-1">
                  Website URL<span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="websiteURL"
                  value={websiteURL || ""}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Enter website URL"
                  className="w-full h-[45px] px-[13px] rounded-[5px] bg-[#FFFFFF] border border-[#D9D9D9] text-[#303030] text-[14px] font-[400] outline-none font-poppins"
                />
                {errors.websiteURL && touched.websiteURL && (
                  <div className="ms-1 text-start w-full">
                    <span className="text-red-600 text-[8px] sm:text-[10px] md:text-[12px] font-[600]">
                      {errors.websiteURL}
                    </span>
                  </div>
                )}
              </div>

              {/* Online Options Cards */}
              <div className="flex flex-col gap-4">
                {radioOnlineOptions.map((option) => {
                  const isSelected = onlineOption === option.value;
                  return (
                    <div
                      key={option.value}
                      className={`w-full p-4 rounded-[10px] border transition-all flex items-start gap-4 cursor-pointer ${isSelected
                        ? "border-[#E0E0E0] bg-white shadow-[0px_8px_24px_rgba(0,0,0,0.08)]"
                        : "border-[#E0E0E0] bg-white hover:border-[#BDBDBD]"
                        }`}
                      onClick={() => setFieldValue("onlineOption", option.value)}
                    >
                      <div className={`w-[20px] h-[20px] rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-[3.5px] ${isSelected ? 'border-[#00ADEF]' : 'border-[#00ADEF] opacity-40'}`}>
                        {isSelected && <div className="w-[14px] h-[14px] rounded-full border-[5px] border-[#00ADEF] bg-white transition-all" />}
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[14px] font-[600] font-poppins text-[#303030]">
                          {option.title}
                        </span>
                        <span className="text-[12px] font-[400] text-[#767676] font-poppins">
                          {option.content}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Code Input Section (Inside Card) */}
              {onlineOption === "ONLINE_WITH_CODE" && (
                <div className="mt-6 pt-6 border-t border-[#F0F0F0] w-full flex flex-col items-start gap-4" onClick={(e) => e.stopPropagation()}>
                  {document === "" && (
                    <div className="w-full">
                      <label className="font-[600] font-poppins text-[#303030] text-[14px] block mb-1">
                        Promo Code(s)
                      </label>
                      <div className="offer-tag-input-wrapper">
                        {Array.isArray(offerCodes) && offerCodes.map((code, idx) => (
                          <div key={idx} className="offer-code-tag">
                            <span>{code}</span>
                            <button type="button" onClick={() => handleRemoveTag(code)}>
                              <CloseIcon style={{ width: 8, height: 8, color: "white" }} />
                            </button>
                          </div>
                        ))}
                        <input
                          type="text"
                          value={tagInput}
                          onChange={(e) => setTagInput(e.target.value)}
                          onKeyDown={handleTagKeyDown}
                          onBlur={() => {
                            if (tagInput.trim()) {
                              const val = tagInput.trim().replace(/,$/, "").toUpperCase();
                              const currentCodes = Array.isArray(offerCodes) ? offerCodes : [];
                              if (!currentCodes.includes(val)) {
                                setFieldValue("offerCodes", [...currentCodes, val]);
                              }
                              setTagInput("");
                            }
                          }}
                          placeholder={Array.isArray(offerCodes) && offerCodes.length > 0 ? "" : "Type Code"}
                          className="offer-tag-input-field"
                        />
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <div className="w-[16px] h-[16px] rounded-full border-[1.2px] border-[#00ADEF] flex items-center justify-center flex-shrink-0">
                          <span className="text-[#00ADEF] text-[10px] font-[600] font-poppins mb-[0.5px]">i</span>
                        </div>
                        <p className="text-[12px] text-[#767676] font-poppins font-[400]">
                          Enter multiple codes, separated by commas.
                        </p>
                      </div>
                      {errors.offerCodes && touched.offerCodes && (
                        <div className="ms-1 text-start w-full">
                          <span className="text-red-600 text-[8px] sm:text-[10px] md:text-[12px] font-[600]">
                            {errors.offerCodes}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {document === "" && (
                    <div className="flex items-center w-full gap-4">
                      <div className="flex-grow h-[1px] bg-[#E0E0E0]"></div>
                      <span className="text-[#767676] font-poppins text-[14px]">Or</span>
                      <div className="flex-grow h-[1px] bg-[#E0E0E0]"></div>
                    </div>
                  )}

                  <CSVUpload
                    document={document}
                    handleAcceptedFiles={handleAcceptedFiles}
                    handleClear={handleClear}
                    offerCodesCount={Array.isArray(offerCodes) ? offerCodes.length : 0}
                  />
                </div>
              )}
            </div>
          )}

          {/* 5. Offer Discount Type (New Card Design) */}
          <div className="mt-8 w-full">
            <p className="text-[14px] font-[600] text-[#303030] font-poppins mb-[4px]">
              Offer Discount Type<span className="text-red-500">*</span>
            </p>
            <div className="bg-white border border-[#F0F0F0] rounded-[15px] p-6 mt-2 shadow-[0px_8px_30px_0px_rgba(0,0,0,0.06)] relative z-10">
              <div className="flex flex-col gap-4">
                {offerDiscountTypeOptions.map((option) => {
                  const isSelected = offerDiscountType === option.value;
                  return (
                    <div
                      key={option.value}
                      className={`w-full p-4 rounded-[10px] border transition-all flex items-start gap-4 cursor-pointer ${isSelected
                        ? "border-[#E0E0E0] bg-white shadow-[0px_8px_24px_rgba(0,0,0,0.08)]"
                        : "border-[#E0E0E0] bg-white hover:border-[#BDBDBD]"
                        }`}
                      onClick={() => handleDiscountTypeChange(option.value)}
                    >
                      {/* 100% Match Radio Button: Top-aligned to stay fixed */}
                      <div className={`w-[20px] h-[20px] rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-[3.5px] ${isSelected ? 'border-[#00ADEF]' : 'border-[#00ADEF] opacity-40'}`}>
                        {isSelected && <div className="w-[14px] h-[14px] rounded-full border-[5px] border-[#00ADEF] bg-white transition-all" />}
                      </div>

                      <div className="flex flex-col w-full">
                        <span className="text-[14px] font-[600] font-poppins text-[#303030]">
                          {option.label}
                        </span>
                        <span className="text-[12px] font-[400] text-[#767676] font-poppins">
                          {option.desc}
                        </span>

                        {isSelected && option.value === "DISCOUNTED_PERCENT" && (
                          <div
                            className="mt-6 w-full"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <p className="text-[14px] font-[600] text-[#303030] font-poppins mb-1">
                              Discount Range<span className="text-red-500">*</span>
                            </p>
                            <div className="px-2">
                              <Slider
                                min={0}
                                max={100}
                                value={rangeValue}
                                onChange={handleRangeChange}
                                onAfterChange={handleRangeAfterChange}
                                tooltip={{ open: false }}
                                className="offer-discount-slider"
                              />
                            </div>
                            {/* Exact Match Percentage Badge below the thumb */}
                            <div className="relative h-[40px] w-full mt-2">
                              <div
                                className="absolute transition-all duration-100 ease-out"
                                style={{
                                  left: `${rangeValue}%`,
                                  transform: "translateX(-50%)",
                                }}
                              >
                                <div className="offer-discount-badge">
                                  {rangeValue}%
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                        {isSelected && option.value === "FIXED_DOLLAR" && (
                          <div
                            className="mt-6 w-full"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <label className="font-[600] font-poppins text-[#303030] text-[14px] block mb-1">
                              Fixed Discount Amount<span className="text-red-500">*</span>
                            </label>
                            <input
                              type="number"
                              name="fixedDollarAmount"
                              value={fixedDollarAmount || ""}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              placeholder="Enter Amount"
                              className="w-full h-[45px] px-[13px] rounded-[5px] bg-[#FFFFFF] border border-[#D9D9D9] text-[#303030] text-[14px] font-[400] outline-none font-poppins"
                            />
                            {errors.fixedDollarAmount && touched.fixedDollarAmount && (
                              <div className="ms-1 text-start w-full">
                                <span className="text-red-600 text-[8px] sm:text-[10px] md:text-[12px] font-[600]">
                                  {errors.fixedDollarAmount}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center gap-2 mt-6 opacity-100 select-none cursor-default">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="#00ADEF" strokeWidth="2" />
                  <path d="M12 16V11" stroke="#00ADEF" strokeWidth="2" strokeLinecap="round" />
                  <path d="M12 8H12.01" stroke="#00ADEF" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <span className="text-[#767676] text-[13px] font-poppins font-[400]">
                  Add more details in offer description
                </span>
              </div>
              {errors.offerDiscountType && touched.offerDiscountType && (
                <div className="text-red-600 sm:text-[10px] md:text-[12px] font-[600] mt-1">
                  {errors.offerDiscountType}
                </div>
              )}


            </div>
          </div>


          {/* {showModal && (
          <CancelPopup
            handleNo={closeModal}
            handleYes={handleShowImageUploader}
            heading={modalContent}
            content={`Do you want to upload a ${modalContent} image?`}
            // image={deleteImg}
            confirmation={true}
          />
        )}
        {uploadOption === "upload" && redemedOffer !== "Alphanumeric Code" && (
          <p className="font-poppins font-[400] sm:text-[10px] md:text-[12px] text-[#767676] mt-6">
            {`Great! Please proceed to upload your ${modalContent} image now.`}
          </p>
        )}

        {uploadOption === "auto-generate" &&
          redemedOffer !== "Alphanumeric Code" && (
            <p className="font-poppins font-[400] sm:text-[10px] md:text-[12px] text-[#767676] mt-6">
              {`No worries! We will automatically generate a 
           ${modalContent} for
            you.`}
            </p>
          )}

        {showUploadDocument && (
          <UploadDocument
            thumbnail={redemptionMethod}
            name={"redemptionMethod"}
            iconText={`Upload ${modalContent}`}
            error={errors?.redemptionMethod}
            touched={touched?.redemptionMethod}
            setFieldValue={setFieldValue}
            parentDivClass={"h-[406px] w-[100%] mt-8"}
            setFileUrl={setRedemptionMethodUrl}
            logoFileImage={redemptionMethodlogoFileImage}
            setLogoFileImage={setRedemptionMethodlogoFileImage}
          />
        )} */}

          {/* Navigation buttons removed as they are provided by the Layout */}
        </div>
      </div >
    </div >
  );
};

export default OfferCreation;
